
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { FileText, AlertCircle, AlertTriangle, CheckCircle, Hash, ClockIcon, History, BrainCircuit } from 'lucide-react';
import { formatDeviceId } from '@/components/telemetry/utils/chartUtils';
import { Device } from '@/components/device-health/gis-map/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { aiPredictions, maintenanceAlerts } from '@/components/dashboard/maintenance/data/maintenanceData';

interface MaintenanceInformationProps {
  device: any;
  gisDeviceData?: Device | null;
}

const MaintenanceInformation: React.FC<MaintenanceInformationProps> = ({ device, gisDeviceData }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [devicePredictions, setDevicePredictions] = useState<any[]>([]);
  
  // Generate device ID if not present
  const deviceId = device.deviceId || gisDeviceData?.id || `DEVICE-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

  // Helper function to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Get relevant AI predictions for this device
  useEffect(() => {
    // Find predictions specific to this device
    const relevantPredictions = aiPredictions.filter(
      prediction => prediction.deviceId === deviceId
    );

    setDevicePredictions(relevantPredictions);
  }, [deviceId]);

  // Get alerts for this device
  const deviceAlerts = maintenanceAlerts.filter(
    alert => alert.deviceId === deviceId && alert.issue
  ).map(alert => alert.issue);

  // Mock maintenance history data (in a real app, this would come from an API)
  const maintenanceHistory = [
    { date: '2024-03-15', action: 'Routine inspection', technician: 'John Smith', notes: 'All systems functioning properly' },
    { date: '2024-02-12', action: 'Firmware update', technician: 'Sarah Johnson', notes: 'Updated to version 3.2.1' },
    { date: '2024-01-05', action: 'Battery replacement', technician: 'Mike Davis', notes: 'Replaced main backup battery' }
  ];

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Maintenance Information</CardTitle>
            <div className="flex items-center text-xs text-muted-foreground">
              <Hash className="h-3.5 w-3.5 mr-1" />
              <span className="font-mono">{formatDeviceId(deviceId)}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between">
              <div className="flex items-center">
                <FileText className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="text-sm font-medium">Last Maintenance</span>
              </div>
              <span className="text-sm">{formatDate(device.maintenanceStatus?.lastMaintenance || '2024-03-15')}</span>
            </div>
            <div className="flex justify-between">
              <div className="flex items-center">
                <FileText className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="text-sm font-medium">Next Scheduled</span>
              </div>
              <span className="text-sm">{formatDate(device.maintenanceStatus?.nextScheduled || '2024-05-15')}</span>
            </div>

            <Separator />

            <div>
              <div className="flex items-center mb-2">
                <AlertCircle className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="text-sm font-medium">Alerts</span>
              </div>
              
              {deviceAlerts && deviceAlerts.length > 0 ? (
                <ul className="space-y-2">
                  {deviceAlerts.map((alert: string, index: number) => (
                    <li key={index} className="text-sm flex items-start">
                      <AlertTriangle className="h-4 w-4 mr-2 text-amber-500 mt-0.5" />
                      <span>{alert}</span>
                    </li>
                  ))}
                </ul>
              ) : device.maintenanceStatus?.alerts && device.maintenanceStatus.alerts.length > 0 ? (
                <ul className="space-y-2">
                  {device.maintenanceStatus.alerts.map((alert: string, index: number) => (
                    <li key={index} className="text-sm flex items-start">
                      <AlertTriangle className="h-4 w-4 mr-2 text-amber-500 mt-0.5" />
                      <span>{alert}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                  <span>No active alerts</span>
                </div>
              )}
            </div>

            {devicePredictions.length > 0 && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center mb-2">
                    <BrainCircuit className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span className="text-sm font-medium">AI Predictions</span>
                  </div>
                  
                  {devicePredictions.map((prediction, index) => (
                    <div key={index} className="mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                      <div className="text-sm font-medium">{prediction.title}</div>
                      <div className="text-xs text-muted-foreground">{prediction.description}</div>
                      {prediction.recommendedAction && (
                        <div className="text-xs text-energy-blue mt-1">
                          <span className="font-medium">Recommendation:</span> {prediction.recommendedAction}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-2"
              onClick={() => setShowHistory(true)}
            >
              <History className="h-4 w-4 mr-2" />
              View Maintenance History
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Maintenance History - {gisDeviceData?.name || device.deviceId}</DialogTitle>
          </DialogHeader>
          
          <div className="relative my-4">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>
            {maintenanceHistory.map((item, index) => (
              <div key={index} className="relative pl-10 pb-6">
                <div className="absolute left-2 top-2 rounded-full w-5 h-5 bg-primary border-4 border-white dark:border-gray-900"></div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium">{item.action}</h3>
                      <p className="text-sm text-muted-foreground">Technician: {item.technician}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(item.date)}</span>
                  </div>
                  {item.notes && (
                    <p className="text-sm mt-2 bg-gray-100 dark:bg-gray-700 p-2 rounded">{item.notes}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MaintenanceInformation;
